import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  REDUCTIONS_LIST_QUERY_KEY,
  REDUCTIONS_QUERY_KEY,
  createReduction,
  createReductionDetailQueryKey,
  createReductionFileQueryKey,
  deleteReduction,
  fetchReductionById,
  fetchReductionDocumentFile,
  fetchReductions,
  fetchReductionsForInjunction,
  fetchReductionsForImposition,
  fetchReductionsForReporting,
  type CreateReductionRequest,
} from "@/api/reductions";
import { invalidateReductionQueries } from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  ReductionEvidenceListModel,
  ReductionEvidenceModel,
  ReductionQueryParams,
} from "@/features/cle-compliance/models/reduction";

export {
  REDUCTIONS_LIST_QUERY_KEY,
  REDUCTIONS_QUERY_KEY,
  createReductionDetailQueryKey,
  createReductionFileQueryKey,
} from "@/api/reductions";

export type { CreateReductionRequest } from "@/api/reductions";

export const useReductions = (params: ReductionQueryParams = {}) =>
  useQuery<PaginatedResponse<ReductionEvidenceListModel>>({
    queryKey: [...REDUCTIONS_LIST_QUERY_KEY, params],
    queryFn: () => fetchReductions(params),
    staleTime: 5 * 60 * 1000,
  });

export const useReductionsForReporting = (reportId: string) =>
  useQuery<ReductionEvidenceListModel[]>({
    queryKey: [...REDUCTIONS_QUERY_KEY, "for-reporting", reportId],
    queryFn: () => fetchReductionsForReporting(reportId),
    staleTime: 5 * 60 * 1000,
  });

export const useReductionsForInjunction = (
  injunctionId: string,
  enabled = true,
) =>
  useQuery<ReductionEvidenceListModel[]>({
    enabled: enabled && injunctionId.length > 0,
    queryKey: [...REDUCTIONS_QUERY_KEY, "for-injunction", injunctionId],
    queryFn: () => fetchReductionsForInjunction(injunctionId),
    staleTime: 5 * 60 * 1000,
  });

export const useReductionsForImposition = (
  impositionId: string,
  enabled = true,
) =>
  useQuery<ReductionEvidenceListModel[]>({
    enabled: enabled && impositionId.length > 0,
    queryKey: [...REDUCTIONS_QUERY_KEY, "for-imposition", impositionId],
    queryFn: () => fetchReductionsForImposition(impositionId),
    staleTime: 5 * 60 * 1000,
  });

export const useReductionDocument = (id: string) =>
  useQuery<ReductionEvidenceModel>({
    enabled: id.length > 0,
    queryKey: createReductionDetailQueryKey(id),
    queryFn: () => fetchReductionById(id),
    staleTime: 5 * 60 * 1000,
  });

export const useReductionDocumentFile = (id: string) =>
  useQuery<Blob>({
    enabled: id.length > 0,
    queryKey: createReductionFileQueryKey(id),
    queryFn: () => fetchReductionDocumentFile(id),
    staleTime: 5 * 60 * 1000,
  });

export const useCreateReduction = () => {
  const queryClient = useQueryClient();

  return useMutation<ReductionEvidenceModel, Error, CreateReductionRequest>({
    mutationFn: createReduction,
    onSuccess: () => {
      invalidateReductionQueries(queryClient);
    },
  });
};

export const useDeleteReduction = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteReduction,
    onSuccess: (_data, id) => {
      invalidateReductionQueries(queryClient, id);
    },
  });
};
